const isPdfOrArxiv = async (url: string) => {
  try {
    if (url.endsWith('.pdf')) {
      return true;
    }

    const controller = new AbortController();
    const response = await fetch(
      `/api/header-proxy?url=${encodeURIComponent(url)}`,
      {
        method: 'HEAD',
        signal: controller.signal,
      },
    );
    const contentType = response.headers.get('content-type');
    controller.abort();
    return (
      contentType?.includes('application/pdf') || contentType?.includes('pdf')
    );
  } catch (error) {
    console.error('Error checking URL:', error);
    return false;
  }
};

export const convertUrlToScrapableUrl = (url: string): string | null => {
  if (!url || url.trim() === '') return url;

  try {
    const arxivRegex = /^https?:\/\/arxiv\.org\/(abs|pdf)\/(\d+\.\d+)/;

    const match = url.match(arxivRegex);
    if (match) {
      return `https://arxiv.org/pdf/${match[2]}`;
    }

    // Already an arXiv PDF URL
    const arxivPdfMatch = url.match(/^https?:\/\/arxiv\.org\/pdf\/(\d+\.\d+)/i);
    if (arxivPdfMatch) {
      return url;
    }

    // bioRxiv and medRxiv
    if (
      url.includes('biorxiv.org/content/') ||
      url.includes('medrxiv.org/content/')
    ) {
      if (!url.endsWith('.full.pdf')) {
        return `${url}.full.pdf`;
      }
      return url;
    }

    // PLOS journals
    if (url.includes('journals.plos.org') && url.includes('article?id=')) {
      const articleId = url.split('?id=')[1];
      if (articleId) {
        const baseUrl = url.split('article')[0];
        return `${baseUrl}article/file?id=${articleId}&type=printable`;
      }
    }

    // ScienceDirect articles
    if (url.includes('sciencedirect.com/science/article')) {
      const piiMatch = url.match(/\/pii\/([^\/]+)/);
      const absMatch = url.match(/\/abs\/pii\/([^\/]+)/);
      const articleMatch = url.match(/\/article\/([^\/]+)$/);

      const articleId = piiMatch?.[1] || absMatch?.[1] || articleMatch?.[1];
      if (articleId) {
        return `https://www.sciencedirect.com/science/article/pii/${articleId}/pdfft`;
      }
    }

    // SSRN papers
    if (
      url.includes('papers.ssrn.com/sol3/') ||
      url.includes('ssrn.com/abstract=')
    ) {
      try {
        const abstractId = new URLSearchParams(new URL(url).search).get(
          'abstract_id',
        );
        if (abstractId) {
          return `https://papers.ssrn.com/sol3/Delivery.cfm/SSRN_ID${abstractId}.pdf`;
        }
      } catch (_) {
        return url;
      }
    }

    // JSTOR articles
    if (url.includes('jstor.org/stable/')) {
      const pathParts = url.split('/');
      const jstorIndex = pathParts.indexOf('stable');
      if (jstorIndex !== -1 && jstorIndex < pathParts.length - 1) {
        const jstorId = pathParts[jstorIndex + 1];
        return `https://www.jstor.org/stable/pdf/${jstorId}.pdf`;
      }
    }

    // ACM Digital Library
    if (url.includes('dl.acm.org/doi/')) {
      const doiMatch = url.match(/\/doi\/([^\/]+\/[^\/]+)/);
      const acmDOI = doiMatch?.[1];
      if (acmDOI) {
        return `https://dl.acm.org/doi/pdf/${acmDOI}`;
      }
    }

    // IEEE Xplore
    if (
      url.includes('ieeexplore.ieee.org/document/') ||
      url.includes('ieeexplore.ieee.org/abstract/document/')
    ) {
      const idMatch = url.match(/\/(?:abstract\/)?document\/(\d+)/);
      const ieeeId = idMatch?.[1];
      if (ieeeId) {
        return `https://ieeexplore.ieee.org/stamp/stamp.jsp?tp=&arnumber=${ieeeId}`;
      }
    }

    // Science.org
    if (url.includes('science.org/doi/')) {
      const doiMatch = url.match(/\/doi\/([^\/]+\/[^\/]+)/);
      const scienceDOI = doiMatch?.[1];
      if (scienceDOI) {
        return `https://www.science.org/doi/pdf/${scienceDOI}`;
      }
    }

    // Wiley Online Library
    if (url.includes('onlinelibrary.wiley.com/doi/')) {
      let wileyDOI = '';
      if (url.includes('/doi/full/')) {
        const doiMatch = url.match(/\/doi\/full\/([^\/\?#]+(?:\/[^\/\?#]+)*)/);
        wileyDOI = doiMatch?.[1] || '';
      } else {
        const doiMatch = url.match(/\/doi\/([^\/]+\/[^\/]+)/);
        wileyDOI = doiMatch?.[1] || '';
      }
      if (wileyDOI) {
        return `https://onlinelibrary.wiley.com/doi/epdf/${wileyDOI}`;
      }
    }

    // Taylor & Francis articles
    if (
      url.includes('tandfonline.com') &&
      (url.includes('/doi/full/') || url.includes('/doi/abs/'))
    ) {
      // Extract DOI from URL path (after /doi/full/ OR /doi/abs/)
      const doiMatch = url.match(
        /\/doi\/(?:full|abs)\/([^\/\?#]+(?:\/[^\/\?#]+)*)/,
      );
      const tandfDOI = doiMatch?.[1] || '';
      if (tandfDOI) {
        return `https://www.tandfonline.com/doi/epdf/${tandfDOI}`;
      }
    }

    return null;
  } catch (_) {
    return null;
  }
};

export const handlePdfUrlChecks = async (
  url: string,
): Promise<{
  isPdf: boolean;
  url: string;
  pdfData?: ArrayBuffer;
  contentType?: string;
}> => {
  if (!url || url.trim() === '') return { isPdf: false, url };

  try {
    if (url.includes('api.zotero')) {
      const response = await fetch(
        `/api/zotero/get-file?url=${encodeURIComponent(url)}`,
        {
          method: 'GET',
        },
      );
      if (response.ok) {
        const pdfData = await response.arrayBuffer();
        const contentType = response.headers.get('Content-Type') || undefined;
        return { isPdf: true, url, pdfData, contentType };
      }
      return { isPdf: false, url };
    }
  } catch (_) {
    return { isPdf: false, url };
  }

  const urlCheck = await isPdfOrArxiv(url);
  if (urlCheck) return { isPdf: true, url };

  const scrapableUrl = convertUrlToScrapableUrl(url);
  if (scrapableUrl) {
    return { isPdf: true, url: scrapableUrl };
  }

  const isDoi = url.includes('doi.org/') || /10\.\d{4,}\/[^\s\/]+/i.test(url);
  const isPmid = url.includes('pubmed.ncbi.nlm.nih.gov/') || /^\d+$/.test(url);

  if (isDoi) {
    let doi = url;
    if (url.includes('doi.org/')) {
      doi = url.split('doi.org/')[1];
    } else {
      const match = url.match(/10\.\d{4,}\/[^\s\/]+/i);
      if (match) {
        doi = match[0];
      }
    }

    try {
      const response = await fetch(
        `/api/check-doi?doi=${encodeURIComponent(doi)}`,
        {
          method: 'GET',
        },
      );

      if (response.ok) {
        const contentType = response.headers.get('content-type');

        // Check if response is a PDF file
        if (contentType?.includes('application/pdf')) {
          // Return the PDF data for direct ingestion
          const pdfData = await response.arrayBuffer();
          return { isPdf: true, url, pdfData };
        }

        // Handle URL response as before
        const pdfUrl = await response.text();
        if (pdfUrl && pdfUrl !== 'null') {
          return { isPdf: true, url: pdfUrl };
        }
      }
    } catch (error) {
      console.error('Error checking DOI:', error);
    }
  }

  if (isPmid) {
    let pmid = url;
    if (url.includes('pubmed.ncbi.nlm.nih.gov/')) {
      const match = url.match(/pubmed\.ncbi\.nlm\.nih\.gov\/(\d+)/);
      if (match) {
        pmid = match[1];
      }
    }

    try {
      const response = await fetch(
        `/api/check-pmid?pmid=${encodeURIComponent(pmid)}`,
        {
          method: 'GET',
        },
      );

      if (response.ok) {
        const contentType = response.headers.get('content-type');

        // Check if response is a PDF file
        if (contentType?.includes('application/pdf')) {
          // Return the PDF data for direct ingestion
          const pdfData = await response.arrayBuffer();
          return { isPdf: true, url, pdfData };
        }

        // Handle URL response as before
        const pdfUrl = await response.text();
        if (pdfUrl && pdfUrl !== 'null') {
          return { isPdf: true, url: pdfUrl };
        }
      }
    } catch (error) {
      console.error('Error checking PMID:', error);
    }
  }

  return { isPdf: false, url };
};
